import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../../api/index.js';
import {
  Box,
  Grid,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Divider,
  Switch,
  Stack,
  Chip,
  Checkbox,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tabs,
  Tab,
  FormHelperText,
} from '@mui/material';

import RefreshIcon from '@mui/icons-material/Refresh';
import CachedIcon from '@mui/icons-material/Cached';
import SyncDisabledIcon from '@mui/icons-material/SyncDisabled';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SelectGroupProvider from '../../dsasComponents/SelectGroupProvider.js';

const DEFAULT_SCOPE = 'default';
const WIDGET_CONFIGURATION_CONFIG_TYPE = 'widget_configuration';
const SIMPLE_WIDGET_OPTIONS = [
  { value: 'team_ranking', label: 'Statistiques équipes' },
  { value: 'player_ranking', label: 'Statistiques joueurs' },
  { value: 'team_standings', label: 'Classement compétition' },
  { value: 'competition_results', label: 'Résultats compétition' },
  { value: 'direct_calendar', label: 'Calendrier direct' },
  // { value: 'direct_match', label: 'Direct match' },
];

const ALL_WIDGET_OPTIONS = [
  ...SIMPLE_WIDGET_OPTIONS,
  { value: 'composed_tabs', label: 'Widget composé (onglets)' },
];

const prettyMode = (m) => {
  if (m === 'on_access') return 'On access';
  if (m === 'hourly') return 'Hourly (on access if >= 1h)';
  return m || '-';
};

const ttlLine = (label, override, fallback) => {
  const hasOverride = override !== null && override !== undefined && override !== '';
  const value = hasOverride ? override : fallback;
  const origin = hasOverride ? 'override' : 'default';
  return { label, value, origin };
};

const fmtDate = (value) => {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString();
  } catch {
    return '-';
  }
};

const fmtDuration = (ms) => {
  if (!Number.isFinite(ms)) return '-';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
};

  const prettyWidgetType = (type) => {
    if (type === 'team_ranking') return 'Statistiques équipes';
    if (type === 'player_ranking') return 'Statistiques joueurs';
    if (type === 'team_standings') return 'Classement compétition';
    if (type === 'competition_results') return 'Résultats compétition';
    if (type === 'composed_tabs') return 'Widget composé (onglets)';
    if (type === 'direct_calendar') return 'Calendrier direct';
    if (type === 'direct_match') return 'Direct match';
    return type || '-';
  };

const buildWidgetSummary = (row) => {
  const ctx = row?.context || {};
  const filters = ctx?.filters || {};

  const parts = [
    row?.widgetType ? `widget: ${prettyWidgetType(row.widgetType)}` : null,
    filters.provider ? `provider: ${filters.provider}` : null,
    filters.discipline ? `discipline: ${filters.discipline}` : null,
    filters.statType ? `stat: ${filters.statType}` : null,
    filters.season ? `season: ${filters.season}` : null,
    Array.isArray(filters.competitionIds) && filters.competitionIds.length
      ? `competitions: ${filters.competitionIds.length}`
      : null,
    filters.day != null ? `day: ${filters.day}` : null,
    filters.page ? `page: ${filters.page}` : null,
    filters.limit ? `limit: ${filters.limit}` : null,
    filters.date ? `date: ${filters.date}` : null,
    filters.calendarDate ? `date: ${filters.calendarDate}` : null,
  ].filter(Boolean);

  return parts.join(' • ');
};

const createEmptyDisciplineFilters = () => ({
  seasonMode: 'explicit',
  allowedSeasons: [],
  defaultSeasons: [],
  seasonSince: '',
  allowedCompetitionIds: [],
  defaultCompetitionIds: [],
});

const normalizeFiltersByDiscipline = (disciplines = [], source = {}) => {
  const result = {};

  disciplines.forEach((discipline) => {
    const existing = source?.[discipline] || {};
    result[discipline] = {
      seasonMode: existing.seasonMode === 'since' ? 'since' : 'explicit',
      allowedSeasons: Array.isArray(existing.allowedSeasons) ? existing.allowedSeasons : [],
      defaultSeasons: Array.isArray(existing.defaultSeasons) ? existing.defaultSeasons : [],
      seasonSince: existing.seasonSince || '',
      allowedCompetitionIds: Array.isArray(existing.allowedCompetitionIds)
        ? existing.allowedCompetitionIds
        : [],
      defaultCompetitionIds: Array.isArray(existing.defaultCompetitionIds)
        ? existing.defaultCompetitionIds
        : [],
    };
  });

  return result;
};

const DEFAULT_DIRECT_CALENDAR_TTL_POLICY_FORM = {
  ttlPolicyMode: 'static',
  ttlPolicyDefaultFreshTtlSec: 120,
  ttlPolicyWindowFromOffsetDays: 0,
  ttlPolicyWindowToOffsetDays: 0,
  ttlPolicyWindowFreshTtlSec: 10,
};

const isFilled = (value) => value !== null && value !== undefined && String(value).trim() !== '';

const prettyDayOffset = (value) => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return '-';
  if (numberValue === 0) return 'J';
  return numberValue > 0 ? `J+${numberValue}` : `J${numberValue}`;
};

const resolveTtlPolicyFormValues = (ttlPolicy = null) => {
  if (ttlPolicy?.mode !== 'relative_date_windows') {
    return { ...DEFAULT_DIRECT_CALENDAR_TTL_POLICY_FORM };
  }

  const firstWindow = Array.isArray(ttlPolicy.windows) && ttlPolicy.windows.length
    ? ttlPolicy.windows[0]
    : {};

  return {
    ttlPolicyMode: 'relative_date_windows',
    ttlPolicyDefaultFreshTtlSec:
      ttlPolicy.defaultFreshTtlSec ?? DEFAULT_DIRECT_CALENDAR_TTL_POLICY_FORM.ttlPolicyDefaultFreshTtlSec,
    ttlPolicyWindowFromOffsetDays:
      firstWindow.fromOffsetDays ?? DEFAULT_DIRECT_CALENDAR_TTL_POLICY_FORM.ttlPolicyWindowFromOffsetDays,
    ttlPolicyWindowToOffsetDays:
      firstWindow.toOffsetDays ?? DEFAULT_DIRECT_CALENDAR_TTL_POLICY_FORM.ttlPolicyWindowToOffsetDays,
    ttlPolicyWindowFreshTtlSec:
      firstWindow.freshTtlSec ?? DEFAULT_DIRECT_CALENDAR_TTL_POLICY_FORM.ttlPolicyWindowFreshTtlSec,
  };
};

const validateTtlPolicyForm = (form) => {
  if (form.ttlPolicyMode !== 'relative_date_windows') return null;

  const from = Number(form.ttlPolicyWindowFromOffsetDays);
  const to = Number(form.ttlPolicyWindowToOffsetDays);
  const windowFresh = Number(form.ttlPolicyWindowFreshTtlSec);

  if (!Number.isInteger(from) || !Number.isInteger(to)) {
    return 'Les bornes de fenêtre TTL doivent être des nombres entiers.';
  }

  if (from > to) {
    return 'La borne de début de fenêtre TTL doit être inférieure ou égale à la borne de fin.';
  }

  if (!Number.isFinite(windowFresh) || windowFresh <= 0) {
    return 'Le TTL de la fenêtre chaude doit être supérieur à 0.';
  }

  if (isFilled(form.ttlPolicyDefaultFreshTtlSec)) {
    const defaultFresh = Number(form.ttlPolicyDefaultFreshTtlSec);
    if (!Number.isFinite(defaultFresh) || defaultFresh <= 0) {
      return 'Le TTL hors fenêtre doit être vide ou supérieur à 0.';
    }
  }

  return null;
};

const buildTtlPolicyPayload = (form) => {
  if (form.ttlPolicyMode !== 'relative_date_windows') {
    return { mode: 'static' };
  }

  const from = Number(form.ttlPolicyWindowFromOffsetDays);
  const to = Number(form.ttlPolicyWindowToOffsetDays);

  return {
    mode: 'relative_date_windows',
    dateSource: 'selectedDate',
    timezone: 'Europe/Paris',
    defaultFreshTtlSec: isFilled(form.ttlPolicyDefaultFreshTtlSec)
      ? Number(form.ttlPolicyDefaultFreshTtlSec)
      : null,
    defaultStaleTtlSec: null,
    windows: [
      {
        label: `${prettyDayOffset(from)} à ${prettyDayOffset(to)}`,
        fromOffsetDays: from,
        toOffsetDays: to,
        freshTtlSec: Number(form.ttlPolicyWindowFreshTtlSec),
        staleTtlSec: null,
      },
    ],
  };
};

const buildTtlPolicyChips = (ttlPolicy = null) => {
  if (ttlPolicy?.mode !== 'relative_date_windows') {
    return ['TTL calendrier: standard'];
  }

  const firstWindow = Array.isArray(ttlPolicy.windows) && ttlPolicy.windows.length
    ? ttlPolicy.windows[0]
    : null;

  return [
    'TTL calendrier: fenêtre relative',
    `Hors fenêtre: ${ttlPolicy.defaultFreshTtlSec ?? '-'}s`,
    firstWindow
      ? `Fenêtre ${prettyDayOffset(firstWindow.fromOffsetDays)} à ${prettyDayOffset(firstWindow.toOffsetDays)}: ${firstWindow.freshTtlSec}s`
      : 'Fenêtre: -',
  ];
};


const normalizeDateString = (value) => {
  const normalized = String(value || '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(normalized) ? normalized : null;
};

const todayDateString = (timeZone = 'Europe/Paris', now = new Date()) => {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);

    const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${byType.year}-${byType.month}-${byType.day}`;
  } catch {
    return now.toISOString().slice(0, 10);
  }
};

const diffDateOnlyDays = (dateString, referenceDateString) => {
  const parseUtc = (value) => {
    const [year, month, day] = String(value).split('-').map(Number);
    return Date.UTC(year, month - 1, day);
  };

  return Math.round((parseUtc(dateString) - parseUtc(referenceDateString)) / 86400000);
};

const resolveEffectiveTtlsForWidgetRow = ({ currentConfig, defaultTtls, row }) => {
  const baseFreshTtl = Number.isFinite(+currentConfig?.freshTtlSec)
    ? +currentConfig.freshTtlSec
    : +defaultTtls.freshTtlSec;

  const baseStaleTtl = Number.isFinite(+currentConfig?.staleTtlSec)
    ? +currentConfig.staleTtlSec
    : +defaultTtls.staleTtlSec;

  const policy = currentConfig?.ttlPolicy;
  const rowWidgetType = row?.widgetType || row?.context?.widgetType || currentConfig?.widgetType;

  if (rowWidgetType !== 'direct_calendar' || policy?.mode !== 'relative_date_windows') {
    return { freshTtl: baseFreshTtl, staleTtl: baseStaleTtl };
  }

  const filters = row?.context?.filters || {};
  const dateSource = ['selectedDate', 'date'].includes(String(policy?.dateSource || ''))
    ? String(policy.dateSource)
    : 'selectedDate';

  const dateValue =
    normalizeDateString(filters?.[dateSource]) ||
    normalizeDateString(filters?.selectedDate) ||
    normalizeDateString(filters?.date);

  if (!dateValue) {
    return { freshTtl: baseFreshTtl, staleTtl: baseStaleTtl };
  }

  const today = todayDateString(policy?.timezone || 'Europe/Paris');
  const offsetDays = diffDateOnlyDays(dateValue, today);
  const matchedWindow = (Array.isArray(policy?.windows) ? policy.windows : []).find(
    (window) =>
      offsetDays >= Number(window?.fromOffsetDays) &&
      offsetDays <= Number(window?.toOffsetDays)
  );

  return {
    freshTtl: Number(
      matchedWindow?.freshTtlSec ??
      policy?.defaultFreshTtlSec ??
      baseFreshTtl
    ),
    staleTtl: Number(
      matchedWindow?.staleTtlSec ??
      policy?.defaultStaleTtlSec ??
      baseStaleTtl
    ),
  };
};

const MAX_MANAGER_COMPETITIONS = 5;

const WidgetCacheManager = () => {


  const isMountedRef = useRef(true);

  const [defaultTtls, setDefaultTtls] = useState({
    freshTtlSec: 60,
    staleTtlSec: 3600,
    refreshMode: 'on_access',
    archived: false,
  });

  const [scope, setScope] = useState(DEFAULT_SCOPE);

  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState('');
  const [copyFeedbackType, setCopyFeedbackType] = useState('success');

  const [configs, setConfigs] = useState([]);
  const [caches, setCaches] = useState([]);
  const [deletingCacheId, setDeletingCacheId] = useState(null);

  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [loadingCaches, setLoadingCaches] = useState(false);
  const [deletingConfigId, setDeletingConfigId] = useState(null);

  const [expandedConfigIds, setExpandedConfigIds] = useState([]);

  const [activeDisciplineTab, setActiveDisciplineTab] = useState('rugby');

  const [seasonOptionsByDiscipline, setSeasonOptionsByDiscipline] = useState({});
  const [competitionOptionsByDiscipline, setCompetitionOptionsByDiscipline] = useState({});

  const [form, setForm] = useState({
    configName: '',
    displayTitleMode: 'default',
    displayTitle: '',
    widgetType: 'team_ranking',
    childWidgetTypes: [],
    provider: 'dsas',
    disciplines: ['rugby'],
    defaultDiscipline: '',
    filtersByDiscipline: {
      rugby: createEmptyDisciplineFilters(),
    },
    calendarDateMode: 'fixed',
    calendarStartDate: '',
    calendarEndDate: '',
    calendarDefaultDate: '',
    directMatchOpenMode: 'cache',
    directMatchLiveWindowDaysBefore: 0,
    directMatchLiveWindowDaysAfter: 0,
    ...DEFAULT_DIRECT_CALENDAR_TTL_POLICY_FORM,
    freshTtlSec: '',
    staleTtlSec: '',
    refreshMode: 'on_access',
    archived: false,
  });

  const [selectedConfig, setSelectedConfig] = useState(null);

  const [refreshReport, setRefreshReport] = useState(null);
  const [refreshingNow, setRefreshingNow] = useState(false);
  const [planningRefresh, setPlanningRefresh] = useState(false);

  const [selectedCacheIds, setSelectedCacheIds] = useState([]);
  const [refreshingSelectedCaches, setRefreshingSelectedCaches] = useState(false);
  const [selectedCachesRefreshReport, setSelectedCachesRefreshReport] = useState(null);
  const [deletingSelectedCaches, setDeletingSelectedCaches] = useState(false);
  const [selectedCachesDeleteReport, setSelectedCachesDeleteReport] = useState(null);

  const resetForm = () => {
    setSelectedConfig(null);
    setRefreshReport(null);
    setSelectedCachesRefreshReport(null);
    setSelectedCachesDeleteReport(null);
    setSelectedCacheIds([]);
    setCaches([]);
    setActiveDisciplineTab('rugby');

    setForm({
      configName: '',
      displayTitleMode: 'default',
      displayTitle: '',
      widgetType: 'team_ranking',
      childWidgetTypes: [],
      provider: 'dsas',
      disciplines: ['rugby'],
      defaultDiscipline: '',
      filtersByDiscipline: {
        rugby: createEmptyDisciplineFilters(),
      },
      calendarDateMode: 'fixed',
      calendarStartDate: '',
      calendarEndDate: '',
      calendarDefaultDate: '',
      directMatchOpenMode: 'cache',
      directMatchLiveWindowDaysBefore: 0,
      directMatchLiveWindowDaysAfter: 1,
      ...DEFAULT_DIRECT_CALENDAR_TTL_POLICY_FORM,
      freshTtlSec: '',
      staleTtlSec: '',
      refreshMode: 'on_access',
      archived: false,
    });

    setScope(DEFAULT_SCOPE);
  };

  const currentConfig = selectedConfig;

  const hasDirectCalendarOptions =
  form.widgetType === 'direct_calendar' ||
  (
    form.widgetType === 'composed_tabs' &&
    Array.isArray(form.childWidgetTypes) &&
    form.childWidgetTypes.includes('direct_calendar')
  );

  const fetchConfigsList = async () => {
    setLoadingConfigs(true);
    try {
      const res = await api.getWidgetConfigurations({});
      const data = Array.isArray(res.data) ? res.data : [];
      if (!isMountedRef.current) return;
      setConfigs(data);
    } catch (e) {
      console.error('Error fetching widget configurations:', e);
      if (!isMountedRef.current) return;
      setConfigs([]);
    } finally {
      if (isMountedRef.current) setLoadingConfigs(false);
    }
  };

  const fetchCachesForSelection = async () => {
  if (!selectedConfig) {
    setCaches([]);
    return;
  }

  setLoadingCaches(true);
  try {
    const deduped = await getCachesForConfig(selectedConfig);
    if (!isMountedRef.current) return;
    setCaches(deduped);
  } catch (e) {
    console.error('Error fetching widget caches:', e);
    if (!isMountedRef.current) return;
    setCaches([]);
  } finally {
    if (isMountedRef.current) setLoadingCaches(false);
  }
};

  const getCachesForConfig = async (config) => {
  const sc = config?.scope || scope || DEFAULT_SCOPE;

  if (!config) return [];

  const whatIds =
    config?.widgetType === 'composed_tabs'
      ? [
          config?.what_ID,
          ...((config?.childWidgets || [])
            .map((child) => child?.widgetConfigurationId)
            .filter(Boolean)),
        ]
      : [config?.what_ID].filter(Boolean);

  const uniqueWhatIds = [...new Set(whatIds.map(String))];

  if (!uniqueWhatIds.length) return [];

  const responses = await Promise.all(
    uniqueWhatIds.map((what_ID) =>
      api.getWidgetCaches({
        what_ID,
        scope: sc,
        configType: WIDGET_CONFIGURATION_CONFIG_TYPE,
        limit: 200,
        skip: 0,
      })
    )
  );

  const merged = responses.flatMap((res) =>
    Array.isArray(res?.data) ? res.data : []
  );

  return Array.from(
    new Map(
      merged.map((row) => [String(row?._id || row?.requestId || Math.random()), row])
    ).values()
  );
};

  const fetchDefaultTtls = async () => {
    try {
      const res = await api.getDefaultCacheTtls({ scope });
      if (!isMountedRef.current) return;
      setDefaultTtls(res.data);
    } catch (e) {
      console.error('Error fetching default ttls:', e);
    }
  };

  const refreshAll = async () => {
    await Promise.all([
      fetchConfigsList(),
      fetchCachesForSelection(),
      fetchDefaultTtls(),
    ]);
    setSelectedCacheIds([]);
  };

  const handleRefreshSelectedCaches = async () => {
    if (!selectedCacheIds.length) {
      alert('Sélectionnez au moins un cache.');
      return;
    }

    setRefreshingSelectedCaches(true);
    setSelectedCachesRefreshReport(null);

    try {
      const res = await api.refreshNowCaches(selectedCacheIds);
      setSelectedCachesRefreshReport(res?.data ?? null);
      await fetchCachesForSelection();
      setSelectedCacheIds([]);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error ?? 'Erreur lors du rafraîchissement des caches sélectionnés.');
    } finally {
      setRefreshingSelectedCaches(false);
    }
  };

  const handleDeleteSelectedCaches = async () => {
    if (!selectedCacheIds.length) {
      alert('Sélectionnez au moins un cache.');
      return;
    }

    const count = selectedCacheIds.length;

    const confirmed = window.confirm(
      `Supprimer ${count} cache${count > 1 ? 's' : ''} sélectionné${count > 1 ? 's' : ''} ?`
    );

    if (!confirmed) return;

    setDeletingSelectedCaches(true);
    setSelectedCachesDeleteReport(null);

    const idsToDelete = [...new Set(selectedCacheIds.map(String))];

    try {
      const settled = await Promise.allSettled(
        idsToDelete.map((cacheId) => api.deleteCache(cacheId))
      );

      const deleted = settled.filter((result) => result.status === 'fulfilled').length;
      const errors = settled.length - deleted;

      const details = settled
        .map((result, index) => {
          if (result.status === 'fulfilled') return null;

          return {
            cacheId: idsToDelete[index],
            error:
              result.reason?.response?.data?.error ||
              result.reason?.message ||
              String(result.reason),
          };
        })
        .filter(Boolean);

      setSelectedCachesDeleteReport({
        requested: idsToDelete.length,
        deleted,
        errors,
        details,
        endedAt: new Date().toISOString(),
      });

      await fetchCachesForSelection();

      setSelectedCacheIds((prev) =>
        prev.filter((id) => !idsToDelete.includes(String(id)))
      );
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error ?? 'Erreur lors de la suppression des caches sélectionnés.');
    } finally {
      setDeletingSelectedCaches(false);
    }
  };

  const handleUpsert = async (e) => {
    e.preventDefault();

    if (!form.configName?.trim()) {
      alert('Veuillez renseigner un nom de configuration.');
      return;
    }

    // check if composed_tabs has at least one child widget type selected
    if (form.widgetType === 'composed_tabs' && !form.childWidgetTypes?.length) {
      alert('Veuillez sélectionner au moins un widget enfant.');
      return;
  }

    if (hasDirectCalendarOptions) {
      const ttlPolicyError = validateTtlPolicyForm(form);
      if (ttlPolicyError) {
        alert(ttlPolicyError);
        return;
      }
    }

    const payload = {
      widgetConfigurationId: currentConfig?.widgetConfigurationId || undefined,
      configName: form.configName.trim(),
      displayTitleMode: form.displayTitleMode,
      displayTitle: form.displayTitle.trim(),
      widgetType: form.widgetType,
      childWidgetTypes: form.widgetType === 'composed_tabs' ? form.childWidgetTypes : [],
      provider: form.provider,
      disciplines: form.disciplines,
      defaultDiscipline: form.defaultDiscipline || null,
      scope: scope || DEFAULT_SCOPE,
      refreshMode: form.refreshMode,
      archived: !!form.archived,
      filtersByDiscipline: form.filtersByDiscipline || {},

      calendarDateMode: hasDirectCalendarOptions ? form.calendarDateMode : undefined,
      calendarStartDate: hasDirectCalendarOptions ? form.calendarStartDate || null : undefined,
      calendarEndDate: hasDirectCalendarOptions ? form.calendarEndDate || null : undefined,
      calendarDefaultDate: hasDirectCalendarOptions ? form.calendarDefaultDate || null : undefined,

      directMatchOpenMode: hasDirectCalendarOptions
      ? form.directMatchOpenMode || 'cache'
      : undefined,

    directMatchLiveWindowDaysBefore: hasDirectCalendarOptions
      ? Number(form.directMatchLiveWindowDaysBefore || 0)
      : undefined,

    directMatchLiveWindowDaysAfter: hasDirectCalendarOptions
      ? Number(form.directMatchLiveWindowDaysAfter || 1)
      : undefined,

    ttlPolicy: hasDirectCalendarOptions
      ? buildTtlPolicyPayload(form)
      : undefined,

    };

    if (String(form.freshTtlSec).trim() !== '') {
      payload.freshTtlSec = Number(form.freshTtlSec);
    }

    if (String(form.staleTtlSec).trim() !== '') {
      payload.staleTtlSec = Number(form.staleTtlSec);
    }

    try {
      await api.upsertWidgetConfiguration(payload);
      await fetchConfigsList();
      await fetchDefaultTtls();
      resetForm();
    } catch (e) {
      console.error('Error upserting widget configuration:', e);
      alert(e?.response?.data?.error ?? 'Erreur lors de la sauvegarde de la configuration widget.');
    }
  };

  const handleRefreshNow = async () => {
    if (!currentConfig?._id) {
      alert('Sélectionnez une configuration.');
      return;
    }

    setRefreshingNow(true);
    setRefreshReport(null);

    try {
      if (currentConfig.widgetType === 'composed_tabs') {
        const relatedCaches = await getCachesForConfig(currentConfig);

        const cacheIds = [...new Set(
          relatedCaches
            .map((row) => row?._id)
            .filter(Boolean)
            .map(String)
        )];

        if (!cacheIds.length) {
          alert("Aucun cache enfant trouvé pour ce widget composé.");
          return;
        }

        const res = await api.refreshNowCaches(cacheIds);
        setRefreshReport(res?.data ?? null);
      } else {
        const res = await api.refreshNowCacheConfig(currentConfig._id);
        setRefreshReport(res?.data ?? null);
      }

      await refreshAll();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error ?? 'Erreur lors du refresh immédiat.');
    } finally {
      setRefreshingNow(false);
    }
  };

  const handlePlanRefresh = async () => {
    if (!currentConfig?._id) return alert('Sélectionnez une configuration.');
    setPlanningRefresh(true);
    try {
      await api.forceRefreshCacheConfig(currentConfig._id);
      await refreshAll();
      resetForm();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error ?? 'Erreur lors de la planification.');
    } finally {
      setPlanningRefresh(false);
    }
  };

  const handlePlanUnRefresh = async () => {
    if (!currentConfig?._id) return alert('Sélectionnez une configuration.');
    setPlanningRefresh(true);
    try {
      await api.forceUnrefreshCacheConfig(currentConfig._id);
      await refreshAll();
      resetForm();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error ?? 'Erreur lors de la déplanification.');
    } finally {
      setPlanningRefresh(false);
    }
  };

  const handleToggleArchive = async () => {
    if (!currentConfig?._id) {
      alert("Aucune config trouvée pour ce widget. Créez-la d’abord via Enregistrer.");
      return;
    }
    try {
      if (currentConfig.archived) await api.unarchiveCacheConfig(currentConfig._id);
      else await api.archiveCacheConfig(currentConfig._id);
      await refreshAll();
      resetForm();
    } catch (e) {
      console.error('Error toggling archive:', e);
      alert(e?.response?.data?.error ?? 'Erreur lors du (un)archive.');
    }
  };

  const handleDeleteCache = async (cacheId, label) => {
    if (!cacheId) return;

    const confirmed = window.confirm(`Supprimer ce cache${label ? ` "${label}"` : ''} ?`);
    if (!confirmed) return;

    setDeletingCacheId(String(cacheId));
    try {
      await api.deleteCache(cacheId);
      setSelectedCacheIds((prev) => prev.filter((id) => id !== String(cacheId)));
      await fetchCachesForSelection();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error ?? 'Erreur lors de la suppression du cache.');
    } finally {
      setDeletingCacheId(null);
    }
  };

const handleDeleteWidgetConfiguration = async (config) => {
  const targetWidgetConfigurationId = String(
    config?.widgetConfigurationId || config?.what_ID || ''
  );

  if (!targetWidgetConfigurationId) return;

  const isComposed = config.widgetType === 'composed_tabs';
  const childIds = isComposed
    ? (config.childWidgets || [])
        .map((child) => child?.widgetConfigurationId)
        .filter(Boolean)
        .map(String)
    : [];

  const confirmed = window.confirm(
    isComposed
      ? `Supprimer la configuration "${config.configName}" et ses ${childIds.length} enfant(s) ?`
      : `Supprimer la configuration "${config.configName}" ?`
  );

  if (!confirmed) return;

  setDeletingConfigId(targetWidgetConfigurationId);

  try {
    for (const childId of childIds) {
      await api.deleteWidgetConfiguration(childId);
    }

    await api.deleteWidgetConfiguration(targetWidgetConfigurationId);

    if (
      selectedConfig &&
      String(
        selectedConfig.widgetConfigurationId ||
        selectedConfig.what_ID ||
        ''
      ) === targetWidgetConfigurationId
    ) {
      resetForm();
    }

    await fetchConfigsList();
    setCaches([]);
  } catch (e) {
    console.error('Error deleting widget configuration:', e);
    alert(
      e?.response?.data?.error ??
        'Erreur lors de la suppression de la configuration widget.'
    );
  } finally {
    setDeletingConfigId(null);
  }
};

  const handleToggleExpandedConfig = (configId) => {
    const id = String(configId);
    setExpandedConfigIds((prev) =>
      prev.includes(id)
        ? prev.filter((existingId) => existingId !== id)
        : [...prev, id]
    );
  };

  useEffect(() => {
    refreshAll();
  }, []);

  useEffect(() => {
    setSelectedCacheIds([]);
    setSelectedCachesRefreshReport(null);
    setSelectedCachesDeleteReport(null);
    fetchCachesForSelection();
    fetchDefaultTtls();
  }, [selectedConfig]);

  useEffect(() => {
    let ignore = false;

    const loadSeasons = async () => {
      const entries = await Promise.all(
        form.disciplines.map(async (discipline) => {
          try {
            const { data } = await api.getCompetitionSeasonsForRankingBuilder({
              provider: form.provider,
              discipline,
            });

            const values = Array.isArray(data)
              ? data.map((v) => String(v).trim()).filter(Boolean)
              : [];

            return [discipline, values];
          } catch {
            return [discipline, []];
          }
        })
      );

      if (!ignore) {
        setSeasonOptionsByDiscipline(Object.fromEntries(entries));
      }
    };

    if (form.provider && form.disciplines.length) {
      loadSeasons();
    } else {
      setSeasonOptionsByDiscipline({});
    }

    return () => {
      ignore = true;
    };
}, [form.provider, form.disciplines]);

useEffect(() => {
  let ignore = false;

  const loadCompetitions = async () => {
    const entries = await Promise.all(
      form.disciplines.map(async (discipline) => {
        const filters = form.filtersByDiscipline?.[discipline] || createEmptyDisciplineFilters();

        try {
          const params = {
            provider: form.provider,
            discipline,
          };

          if (filters.seasonMode === 'since' && filters.seasonSince) {
            params.seasonSince = filters.seasonSince;
          } else if (
            filters.seasonMode === 'explicit' &&
            Array.isArray(filters.allowedSeasons) &&
            filters.allowedSeasons.length
          ) {
            params.season = filters.allowedSeasons.join(',');
          }

          const { data } = await api.getCompetitionsForRankingBuilder(params);

          return [discipline, Array.isArray(data) ? data : []];
        } catch {
          return [discipline, []];
        }
      })
    );

    if (!ignore) {
      setCompetitionOptionsByDiscipline(Object.fromEntries(entries));
    }
  };

  if (form.provider && form.disciplines.length) {
    loadCompetitions();
  } else {
    setCompetitionOptionsByDiscipline({});
  }

  return () => {
    ignore = true;
  };
}, [form.provider, form.disciplines, form.filtersByDiscipline]);

useEffect(() => {
  setForm((prev) => {
    let changed = false;
    const nextFiltersByDiscipline = { ...prev.filtersByDiscipline };

    prev.disciplines.forEach((discipline) => {
      const filters = nextFiltersByDiscipline[discipline] || createEmptyDisciplineFilters();
      const availableSeasons = seasonOptionsByDiscipline[discipline] || [];

      const nextAllowedSeasons =
        filters.seasonMode === 'explicit'
          ? (filters.allowedSeasons || []).filter((season) =>
              availableSeasons.includes(season)
            )
          : [];

      const nextDefaultSeasons =
        filters.seasonMode === 'explicit'
          ? (filters.defaultSeasons || []).filter((season) =>
              nextAllowedSeasons.includes(season)
            )
          : [];

      if (
        nextAllowedSeasons.length !== (filters.allowedSeasons || []).length ||
        nextDefaultSeasons.length !== (filters.defaultSeasons || []).length
      ) {
        changed = true;
        nextFiltersByDiscipline[discipline] = {
          ...filters,
          allowedSeasons: nextAllowedSeasons,
          defaultSeasons: nextDefaultSeasons,
        };
      }
    });

    return changed
      ? { ...prev, filtersByDiscipline: nextFiltersByDiscipline }
      : prev;
  });
}, [seasonOptionsByDiscipline]);

useEffect(() => {
  setForm((prev) => {
    let changed = false;
    const nextFiltersByDiscipline = { ...prev.filtersByDiscipline };

    prev.disciplines.forEach((discipline) => {
      const filters = nextFiltersByDiscipline[discipline] || createEmptyDisciplineFilters();
      const availableCompetitionIds = (competitionOptionsByDiscipline[discipline] || []).map(
        (competition) => String(competition._id)
      );

      const nextAllowedCompetitionIds = (filters.allowedCompetitionIds || []).filter((id) =>
        availableCompetitionIds.includes(String(id))
      );

      const nextDefaultCompetitionIds = (filters.defaultCompetitionIds || []).filter((id) =>
        nextAllowedCompetitionIds.includes(String(id))
      );

      if (
        nextAllowedCompetitionIds.length !== (filters.allowedCompetitionIds || []).length ||
        nextDefaultCompetitionIds.length !== (filters.defaultCompetitionIds || []).length
      ) {
        changed = true;
        nextFiltersByDiscipline[discipline] = {
          ...filters,
          allowedCompetitionIds: nextAllowedCompetitionIds,
          defaultCompetitionIds: nextDefaultCompetitionIds,
        };
      }
    });

    return changed
      ? { ...prev, filtersByDiscipline: nextFiltersByDiscipline }
      : prev;
  });
}, [competitionOptionsByDiscipline]);

  const effectiveFreshTtl = Number.isFinite(+currentConfig?.freshTtlSec)
    ? +currentConfig.freshTtlSec
    : +defaultTtls.freshTtlSec;

  const effectiveStaleTtl = Number.isFinite(+currentConfig?.staleTtlSec)
    ? +currentConfig.staleTtlSec
    : +defaultTtls.staleTtlSec;

  const effectiveArchived = !!(currentConfig?.archived || defaultTtls?.archived);

  const ttlSummary = useMemo(() => {
    return [
      ttlLine('OK (récent)', currentConfig?.freshTtlSec, defaultTtls?.freshTtlSec),
      ttlLine('À rafraîchir', currentConfig?.staleTtlSec, defaultTtls?.staleTtlSec),
    ];
  }, [currentConfig, defaultTtls]);

  const formatAge = (ageSec) => {
    if (!Number.isFinite(ageSec)) return '';
    if (ageSec < 60) return `${Math.floor(ageSec)}s`;
    if (ageSec < 3600) return `${Math.floor(ageSec / 60)}m`;
    return `${Math.floor(ageSec / 3600)}h`;
  };

  const computeStateLabel = (row) => {
    if (effectiveArchived || row.archived) {
      return row.isProcessing ? 'Archivé (rafraîchissement…)' : 'Archivé';
    }

    const created = row?.createdAt ? new Date(row.createdAt).getTime() : null;
    if (!created) return row.isProcessing ? 'Inconnu (rafraîchissement)' : 'Inconnu';

    const ageSec = (Date.now() - created) / 1000;

    const { freshTtl, staleTtl } = resolveEffectiveTtlsForWidgetRow({
      currentConfig,
      defaultTtls,
      row,
    });

    let base =
      ageSec < freshTtl ? 'OK (récent)'
      : ageSec < staleTtl ? 'A rafraîchir'
      : 'Très ancien';

    if (row.isProcessing) base += ' (rafraîchissement…)';
    return base;
  };

  const sortedCaches = useMemo(() => {
    const rows = [...(caches || [])].map((row) => {
      const created = row?.createdAt ? new Date(row.createdAt).getTime() : null;
      const ageSec = created ? (Date.now() - created) / 1000 : null;
      const contextSummary = buildWidgetSummary(row);
      return {
        ...row,
        _context_summary: contextSummary,
        _ageSec: ageSec,
        _ageLabel: ageSec != null ? formatAge(ageSec) : '',
        _state: computeStateLabel(row),
      };
    });

    rows.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    return rows;
  }, [caches, currentConfig, defaultTtls]);

  const allVisibleCacheIds = useMemo(() => {
    return sortedCaches
      .map((row) => (row._id ? String(row._id) : null))
      .filter(Boolean);
  }, [sortedCaches]);

  const getWidgetConfigIdentity = (row) => {
  return String(
    row?.widgetConfigurationId ||
    row?.what_ID ||
    row?._id ||
    ''
  );
};

  const childWidgetConfigurationIds = useMemo(() => {
  const ids = new Set();

  (configs || []).forEach((config) => {
    if (config?.widgetType !== 'composed_tabs') return;

    (config?.childWidgets || []).forEach((child) => {
      if (child?.widgetConfigurationId) {
        ids.add(String(child.widgetConfigurationId));
      }
    });
  });

  return ids;
}, [configs]);
  

  const configsSorted = useMemo(() => {
    const rows = Array.isArray(configs) ? configs : [];

    const topLevelRows = rows.filter((row) => {
      const rowId = getWidgetConfigIdentity(row);
      if (!rowId) return true;
      return !childWidgetConfigurationIds.has(rowId);
    });

    return [...topLevelRows].sort((a, b) =>
      String(a.configName || '').localeCompare(String(b.configName || ''))
    );
  }, [configs, childWidgetConfigurationIds]);

  const allCachesSelected =
    allVisibleCacheIds.length > 0 &&
    allVisibleCacheIds.every((id) => selectedCacheIds.includes(id));

  const someCachesSelected =
    allVisibleCacheIds.some((id) => selectedCacheIds.includes(id)) && !allCachesSelected;

  const handleToggleOneCache = (cacheId) => {
    const id = String(cacheId);
    setSelectedCacheIds((prev) =>
      prev.includes(id)
        ? prev.filter((existingId) => existingId !== id)
        : [...prev, id]
    );
  };

  const handleToggleAllCaches = () => {
    setSelectedCacheIds((prev) => {
      if (allCachesSelected) return [];
      return allVisibleCacheIds;
    });
  };


  const getFinalUrl = () => {
    const baseUrl = process.env.NODE_ENV === "development"?  "http://localhost:3006" : process.env.REACT_APP_LIVE_CLIENT_URL  || window.location.origin;

  const raw = currentConfig?.builderPath || '';
  if (!raw) return '';

  try {
    return new URL(raw, baseUrl).toString();
  } catch {
    return raw;
  }
};

  const copyToClipboard = async (value, successMessage) => {
    if (!value) {
      setCopyFeedbackType('error');
      setCopyFeedback("Aucune URL disponible pour l'export.");
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setCopyFeedbackType('success');
      setCopyFeedback(successMessage);
    } catch {
      setCopyFeedbackType('error');
      setCopyFeedback("Impossible de copier dans le presse-papiers.");
    }
  };

  const onCopyIframeSmartScroll = async () => {
    const finalUrl = getFinalUrl();

    const responsiveIframe =
      '<script src="https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/4.3.6/iframeResizer.min.js"></script>' +
      '<script>if(!window._rsz){window._rsz = function () {' +
      'let scrollPending = false;' +
      'let lastIframe = null;' +
      'iFrameResize({' +
      'checkOrigin: false,' +
      'interval: 100,' +
      'messageCallback: function(messageData) {' +
      'const message = messageData.message;' +
      'const iframe = messageData.iframe;' +
      'if (!message) return;' +
      'if (message.type === "scrollToTop") {' +
      'scrollPending = true;' +
      'lastIframe = iframe;' +
      '}' +
      'if (message.type === "scrollToTopReady" && scrollPending && iframe === lastIframe) {' +
      'setTimeout(() => {' +
      'window.scrollTo({' +
      'top: iframe.offsetTop,' +
      'behavior: "smooth"' +
      '});' +
      'scrollPending = false;' +
      'lastIframe = null;' +
      '}, 50);' +
      '}' +
      '}' +
      '});' +
      '};if(document.readyState!="loading"){_rsz()}else{document.addEventListener("DOMContentLoaded",_rsz)}}<\/script>' +
      '<iframe src="' +
      finalUrl +
      '" style="display: block; width: 100%; overflow: auto; margin: auto; border-width: 0px;" scrolling="no"></iframe>';

    await copyToClipboard(
      responsiveIframe,
      'Iframe avec scroll intelligent copiée.'
    );
  };

  const onCopyIframe = async () => {
    const finalUrl = getFinalUrl();

    const responsiveIframe =
      '<script src="https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/4.3.6/iframeResizer.min.js"></script>' +
      '<script>if(!window._rsz){window._rsz=function(){iFrameResize({checkOrigin:false,interval:100});};if(document.readyState!="loading"){_rsz()}else{document.addEventListener("DOMContentLoaded",_rsz)}}</script>' +
      '<iframe src="' +
      finalUrl +
      '" style="display: block; width: 100%; overflow: auto; margin: auto; border-width: 0px;" scrolling="no"></iframe>';

    await copyToClipboard(
      responsiveIframe,
      'Iframe copiée.'
    );
  };

  const onCopyURL = async () => {
    const finalUrl = getFinalUrl();
    await copyToClipboard(finalUrl, 'URL copiée.');
  };

  const handleOpenExportDialog = () => {
    setCopyFeedback('');
    setCopyFeedbackType('success');
    setExportDialogOpen(true);
  };

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h4">Widget Cache Manager</Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshAll}>
          Rafraîchir la page
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Cache global (valeurs par défaut)
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          Ces paramètres s’appliquent aux widgets sans configuration spécifique.
        </Typography>

        <Stack spacing={0.5}>
          <Typography variant="body2">
            <strong>OK (récent) ({defaultTtls?.freshTtlSec}s)</strong> — durée pendant laquelle les données sont servies sans recalcul.
          </Typography>
          <Typography variant="body2">
            <strong>À rafraîchir ({defaultTtls?.staleTtlSec}s)</strong> — durée pendant laquelle les données peuvent être servies pendant un rafraîchissement en arrière-plan.
          </Typography>
          <Typography variant="body2">
            <strong>Mode ({prettyMode(defaultTtls?.refreshMode)})</strong> — stratégie déclenchant le recalcul du cache.
          </Typography>
        </Stack>
      </Paper>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 2.5 }}>
            <Typography variant="h6" gutterBottom>
              {`${selectedConfig ? 'Édition' : 'Création'} de configuration widget`}
            </Typography>

            <Button type="button" variant="contained" onClick={resetForm}>
              Réinitialiser le formulaire
            </Button>

            <Box
              component="form"
              onSubmit={handleUpsert}
              sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 3 }}
            >

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nom de configuration"
                    value={form.configName}
                    onChange={(e) => setForm((s) => ({ ...s, configName: e.target.value }))}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Type de widget</InputLabel>
                    <Select
                      label="Type de widget"
                      value={form.widgetType}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          widgetType: e.target.value,
                          childWidgetTypes:
                            e.target.value === 'composed_tabs' ? s.childWidgetTypes || [] : [],
                        }))
                      }
                    >
                      {ALL_WIDGET_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

              <Grid item xs={12} md={12}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Titre affiché</InputLabel>
                  <Select
                    label="Titre affiché"
                    value={form.displayTitleMode}
                    onChange={(e) =>
                      setForm((s) => ({
                        ...s,
                        displayTitleMode: e.target.value,
                        displayTitle: e.target.value === 'custom' ? s.displayTitle : '',
                      }))
                    }
                  >
                    <MenuItem value="default">Défaut / nom compétition</MenuItem>
                    <MenuItem value="custom">Personnalisé</MenuItem>
                    <MenuItem value="none">Aucun</MenuItem>
                  </Select>
                </FormControl>

                {form.displayTitleMode === 'custom' ? (
                  <TextField
                    fullWidth
                    size="small"
                    label="Texte du titre"
                    value={form.displayTitle}
                    onChange={(e) => setForm((s) => ({ ...s, displayTitle: e.target.value }))}
                  />
                ) : null}
                </Grid>

                {form.widgetType === 'composed_tabs' ? (
                  <Grid item xs={12} md={6}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Widgets enfants</InputLabel>
                      <Select
                        multiple
                        label="Widgets enfants"
                        value={form.childWidgetTypes || []}
                        onChange={(e) => {
                          const nextValues = Array.isArray(e.target.value) ? e.target.value : [];
                          setForm((s) => ({
                            ...s,
                            childWidgetTypes: nextValues,
                          }));
                        }}
                        renderValue={(selected) =>
                          selected
                            .map(
                              (value) =>
                                SIMPLE_WIDGET_OPTIONS.find((option) => option.value === value)?.label || value
                            )
                            .join(', ')
                        }
                      >
                        {SIMPLE_WIDGET_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ) : null}

                <Grid item xs={12} md={6}>
                  <Box sx={{ '& .MuiFormControl-root': { width: '100%' } }}>
                    <SelectGroupProvider
                      value={form.provider}
                      onChange={(e) => setForm((s) => ({ ...s, provider: e.target.value }))}
                    />
                  </Box>
                </Grid>

                 <Grid item xs={12} md={12} sx={{borderTop:'1px solid grey', borderColor:'divider', mt:3, ml:2}}>
                 </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Disciplines</InputLabel>
                    <Select
                      multiple
                      label="Disciplines"
                      value={form.disciplines}
                      onChange={(e) => {
                        const nextValues = Array.isArray(e.target.value) ? e.target.value : [];

                        setForm((s) => ({
                          ...s,
                          disciplines: nextValues,
                          defaultDiscipline: nextValues.includes(s.defaultDiscipline)
                            ? s.defaultDiscipline
                            : '',
                          filtersByDiscipline: normalizeFiltersByDiscipline(
                            nextValues,
                            s.filtersByDiscipline
                          ),
                        }));

                        setActiveDisciplineTab((prev) =>
                          nextValues.includes(prev) ? prev : nextValues[0] || ''
                        );
                      }}
                      renderValue={(selected) => selected.join(', ')}
                    >
                      <MenuItem value="rugby">rugby</MenuItem>
                      <MenuItem value="soccer">soccer</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {form.disciplines.length > 1 ? (
                  <Grid item xs={12} md={6}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Discipline par défaut</InputLabel>
                      <Select
                        label="Discipline par défaut"
                        value={form.defaultDiscipline}
                        onChange={(e) =>
                          setForm((s) => ({ ...s, defaultDiscipline: e.target.value }))
                        }
                      >
                        <MenuItem value="">
                          <em>Aucune</em>
                        </MenuItem>
                        {form.disciplines.map((discipline) => (
                          <MenuItem key={discipline} value={discipline}>
                            {discipline}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ) : null}
              </Grid>

            {hasDirectCalendarOptions ? (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }}>
                        <Chip
                          size="small"
                          label={
                            form.widgetType === 'composed_tabs'
                              ? 'Widget calendrier - dates'
                              : 'Options calendrier'
                          }
                        />
                      </Divider>

                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Ces dates configurent la plage disponible pour le widget calendrier direct.
                        {form.widgetType === 'composed_tabs'
                          ? ' Elles seront appliquées à l’enfant Calendrier direct généré automatiquement.'
                          : ''}
                      </Typography>
                    </Grid>

                    <Grid container spacing={2}>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="Date début"
                        InputLabelProps={{ shrink: true }}
                        value={form.calendarStartDate}
                        onChange={(e) =>
                          setForm((s) => ({ ...s, calendarStartDate: e.target.value }))
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="Date fin"
                        InputLabelProps={{ shrink: true }}
                        value={form.calendarEndDate}
                        onChange={(e) =>
                          setForm((s) => ({ ...s, calendarEndDate: e.target.value }))
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label="Date par défaut"
                        InputLabelProps={{ shrink: true }}
                        value={form.calendarDefaultDate}
                        onChange={(e) =>
                          setForm((s) => ({ ...s, calendarDefaultDate: e.target.value }))
                        }
                        helperText="Optionnel : sinon date du jour ou la plus proche de la date du jour"
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ paddingBottom:2, paddingTop:0, marginTop:0 }} >
                      <Chip size="small" label={
                            form.widgetType === 'composed_tabs'
                              ? 'Widget calendrier - ouverture du direct'
                              : 'Ouverture du direct (type de caches)'
                          } />
                  </Divider>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Ouverture du direct</InputLabel>
                        <Select
                          label="Ouverture du direct"
                          value={form.directMatchOpenMode || 'cache'}
                          onChange={(e) =>
                            setForm((s) => ({
                              ...s,
                              directMatchOpenMode: e.target.value,
                            }))
                          }
                        >
                          <MenuItem value="cache">Cache</MenuItem>
                          <MenuItem value="websocket">Temps réel websocket</MenuItem>
                          <MenuItem value="hybrid">Hybride</MenuItem>
                        </Select>
                        <FormHelperText>
                          Détermine la page ouverte au clic sur un match du calendrier.
                        </FormHelperText>
                      </FormControl>
                    </Grid>

                    {form.directMatchOpenMode === 'hybrid' ? (
                      <>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Fenêtre websocket avant aujourd’hui"
                            value={form.directMatchLiveWindowDaysBefore}
                            onChange={(e) =>
                              setForm((s) => ({
                                ...s,
                                directMatchLiveWindowDaysBefore: e.target.value,
                              }))
                            }
                            inputProps={{ min: 0 }}
                            helperText="Nombre de jours avant la date du match où le websocket est utilisé."
                          />
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Fenêtre websocket après aujourd’hui"
                            value={form.directMatchLiveWindowDaysAfter}
                            onChange={(e) =>
                              setForm((s) => ({
                                ...s,
                                directMatchLiveWindowDaysAfter: e.target.value,
                              }))
                            }
                            inputProps={{ min: 0 }}
                            helperText="Nombre de jours après la date du match où le websocket est utilisé."
                          />
                        </Grid>
                      </>
                    ) : null}

                    </Grid>

                    <Divider sx={{ paddingBottom: 2, paddingTop: 0, marginTop: 2 }}>
                      <Chip size="small" label="TTL (rafraichissement cache) calendrier direct" />
                    </Divider>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Politique TTL</InputLabel>
                          <Select
                            label="Politique TTL"
                            value={form.ttlPolicyMode || 'static'}
                            onChange={(e) =>
                              setForm((s) => ({
                                ...s,
                                ttlPolicyMode: e.target.value,
                              }))
                            }
                          >
                            <MenuItem value="static">Standard</MenuItem>
                            <MenuItem value="relative_date_windows">Fenêtre relative à aujourd’hui</MenuItem>
                          </Select>
                          <FormHelperText>
                            Standard : utilise la durée de rafraichissement classique. Fenêtre relative à aujourd’hui : permet de définir une fenêtre autour de la date du jour avec un temps de rafraichissement customisé. 
                          </FormHelperText>
                        </FormControl>
                      </Grid>

                      {form.ttlPolicyMode === 'relative_date_windows' ? (
                        <>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="TTL hors fenêtre (sec)"
                              value={form.ttlPolicyDefaultFreshTtlSec}
                              onChange={(e) =>
                                setForm((s) => ({
                                  ...s,
                                  ttlPolicyDefaultFreshTtlSec: e.target.value,
                                }))
                              }
                              inputProps={{ min: 1 }}
                              helperText="Temps par défaut, si aucune fenêtre ne correspond."
                            />
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="TTL fenêtre (sec)"
                              value={form.ttlPolicyWindowFreshTtlSec}
                              onChange={(e) =>
                                setForm((s) => ({
                                  ...s,
                                  ttlPolicyWindowFreshTtlSec: e.target.value,
                                }))
                              }
                              inputProps={{ min: 1 }}
                              helperText="Temps de rafraichissement pour les éléments dans la fenêtre."
                            />
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Fenêtre avant aujourd'hui (J-n)"
                              value={form.ttlPolicyWindowFromOffsetDays}
                              onChange={(e) =>
                                setForm((s) => ({
                                  ...s,
                                  ttlPolicyWindowFromOffsetDays: e.target.value,
                                }))
                              }
                              helperText="Ex: -3 pour J-3."
                            />
                          </Grid>

                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Fenêtre après aujourd'hui (J+n)"
                              value={form.ttlPolicyWindowToOffsetDays}
                              onChange={(e) =>
                                setForm((s) => ({
                                  ...s,
                                  ttlPolicyWindowToOffsetDays: e.target.value,
                                }))
                              }
                              helperText="Ex: 1 pour J+1."
                            />
                          </Grid>

                          <Grid item xs={12} md={8}>
                            <Alert severity="info">
                              Fenêtre actuelle : {prettyDayOffset(form.ttlPolicyWindowFromOffsetDays)} à{' '}
                              {prettyDayOffset(form.ttlPolicyWindowToOffsetDays)} =&gt;{' '}
                              {form.ttlPolicyWindowFreshTtlSec || '-'}s. Le reste utilise le TTL hors fenêtre.
                            </Alert>
                          </Grid>
                        </>
                      ) : null}
                    </Grid>
                    
                  </>
                ) : null}
              

              {form.disciplines.length > 0 ? (
                <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#edecec' }}>
                  {form.disciplines.length > 1 ? (
                    <Tabs
                      value={activeDisciplineTab}
                      onChange={(_, value) => setActiveDisciplineTab(value)}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{ mb: 2 }}
                    >
                      {form.disciplines.map((discipline) => (
                        <Tab
                          key={discipline}
                          value={discipline}
                          label={
                            discipline === form.defaultDiscipline
                              ? `${discipline} (défaut)`
                              : discipline
                          }
                        />
                      ))}
                    </Tabs>
                  ) : null}

                  {form.disciplines
                    .filter((discipline) =>
                      form.disciplines.length > 1 ? discipline === activeDisciplineTab : true
                    )
                    .map((discipline) => {
                      const filters =
                        form.filtersByDiscipline?.[discipline] || createEmptyDisciplineFilters();

                      const seasonOptions = seasonOptionsByDiscipline[discipline] || [];
                      const competitionOptions = competitionOptionsByDiscipline[discipline] || [];

                      const competitionNameById = new Map(
                        competitionOptions.map((competition) => [
                          String(competition._id),
                          competition?.season
                            ? `${competition.name} (${competition.season})`
                            : competition.name,
                        ])
                      );

                      return (
                        <Stack key={discipline} spacing={2}>
                          <Typography variant="subtitle2">
                            Paramètres pour {discipline}
                          </Typography>

                          <FormControl size="small" fullWidth>
                          <InputLabel>Mode saisons</InputLabel>
                          <Select
                            label="Mode saisons"
                            value={filters.seasonMode}
                            onChange={(e) =>
                              setForm((s) => ({
                                ...s,
                                filtersByDiscipline: {
                                  ...s.filtersByDiscipline,
                                  [discipline]: {
                                    ...s.filtersByDiscipline[discipline],
                                    seasonMode: e.target.value,
                                    allowedSeasons:
                                      e.target.value === 'since'
                                        ? []
                                        : s.filtersByDiscipline[discipline]?.allowedSeasons || [],
                                    defaultSeasons:
                                      e.target.value === 'since'
                                        ? []
                                        : s.filtersByDiscipline[discipline]?.defaultSeasons || [],
                                    seasonSince:
                                      e.target.value === 'since'
                                        ? s.filtersByDiscipline[discipline]?.seasonSince || ''
                                        : '',
                                    allowedCompetitionIds: [],
                                    defaultCompetitionIds: [],
                                  },
                                },
                              }))
                            }
                          >
                            <MenuItem value="explicit">Sélection explicite</MenuItem>
                            <MenuItem value="since">À partir de (seasonSince)</MenuItem>
                          </Select>
                        </FormControl>

                        {filters.seasonMode === 'since' ? (
                          <TextField
                            size="small"
                            fullWidth
                            label="Season since"
                            value={filters.seasonSince}
                            onChange={(e) =>
                              setForm((s) => ({
                                ...s,
                                filtersByDiscipline: {
                                  ...s.filtersByDiscipline,
                                  [discipline]: {
                                    ...s.filtersByDiscipline[discipline],
                                    seasonSince: e.target.value,
                                    allowedCompetitionIds: [],
                                    defaultCompetitionIds: [],
                                  },
                                },
                              }))
                            }
                            placeholder="ex: 2023"
                          />
                        ) : (
                          <>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Restriction saison(s)</InputLabel>
                              <Select
                                multiple
                                label="Restriction saison(s)"
                                value={filters.allowedSeasons}
                                onChange={(e) => {
                                  const nextValues = Array.isArray(e.target.value)
                                    ? e.target.value
                                    : [];
                                  setForm((s) => ({
                                    ...s,
                                    filtersByDiscipline: {
                                      ...s.filtersByDiscipline,
                                      [discipline]: {
                                        ...s.filtersByDiscipline[discipline],
                                        allowedSeasons: nextValues,
                                        defaultSeasons: (
                                          s.filtersByDiscipline[discipline]?.defaultSeasons || []
                                        ).filter((value) => nextValues.includes(value)),
                                        allowedCompetitionIds: [],
                                        defaultCompetitionIds: [],
                                      },
                                    },
                                  }));
                                }}
                                renderValue={(selected) => selected.join(', ')}
                              >
                                {seasonOptions.map((season) => (
                                  <MenuItem key={season} value={season}>
                                    {season}
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText>
                                Sélectionner les saisons que vous souhaitez afficher. Par défaut, on affiche toutes les saisons existantes et futures du provider.
                              </FormHelperText>
                            </FormControl>

                            <FormControl size="small" fullWidth>
                              <InputLabel>Saison(s) par défaut</InputLabel>
                              <Select
                                multiple
                                label="Saison(s) par défaut"
                                value={filters.defaultSeasons}
                                onChange={(e) => {
                                  const nextValues = Array.isArray(e.target.value)
                                    ? e.target.value
                                    : [];
                                  setForm((s) => ({
                                    ...s,
                                    filtersByDiscipline: {
                                      ...s.filtersByDiscipline,
                                      [discipline]: {
                                        ...s.filtersByDiscipline[discipline],
                                        defaultSeasons: nextValues.filter((value) =>
                                          (
                                            s.filtersByDiscipline[discipline]?.allowedSeasons || []
                                          ).includes(value)
                                        ),
                                      },
                                    },
                                  }));
                                }}
                                renderValue={(selected) => selected.join(', ')}
                              >
                                {(filters.allowedSeasons || []).map((season) => (
                                  <MenuItem key={season} value={season}>
                                    {season}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </>
                        )}

                        <Divider />

                        <FormControl size="small" fullWidth>
                          <InputLabel>Restriction compétition(s)</InputLabel>
                          <Select
                            multiple
                            label="Restriction compétition(s)"
                            value={filters.allowedCompetitionIds}
                            onChange={(e) => {
                              const nextValues = Array.isArray(e.target.value)
                              ? e.target.value.slice(0, MAX_MANAGER_COMPETITIONS)
                              : [];
                              setForm((s) => ({
                                ...s,
                                filtersByDiscipline: {
                                  ...s.filtersByDiscipline,
                                  [discipline]: {
                                    ...s.filtersByDiscipline[discipline],
                                    allowedCompetitionIds: nextValues,
                                    defaultCompetitionIds: (
                                      s.filtersByDiscipline[discipline]?.defaultCompetitionIds || []
                                    ).filter((value) => nextValues.includes(value)),
                                  },
                                },
                              }));
                            }}
                            renderValue={(selected) =>
                              selected
                                .map((id) => competitionNameById.get(String(id)) || String(id))
                                .join(', ')
                            }
                          >
                            {competitionOptions.map((competition) => {
                              const value = String(competition._id);
                              const label = competition?.season
                                ? `${competition.name} (${competition.season})`
                                : competition.name;

                              return (
                                <MenuItem key={value} value={value}>
                                  {label}
                                </MenuItem>
                              );
                            })}
                          </Select>
                          <FormHelperText>
                            Sélectionner les  compétitions que vous souhaitez afficher. Par défaut, on affiche toutes les compétitions existantes et futures du provider pour les saisons sélectionnées.
                          </FormHelperText>
                        </FormControl>

                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Button
                              type="button"
                              variant="outlined"
                              size="small"
                              onClick={() => {
                                const allIds = competitionOptions
                                  .map((competition) => String(competition._id))
                                  .slice(0, MAX_MANAGER_COMPETITIONS);

                                setForm((s) => ({
                                  ...s,
                                  filtersByDiscipline: {
                                    ...s.filtersByDiscipline,
                                    [discipline]: {
                                      ...s.filtersByDiscipline[discipline],
                                      allowedCompetitionIds: allIds,
                                      defaultCompetitionIds: (
                                        s.filtersByDiscipline[discipline]?.defaultCompetitionIds || []
                                      ).filter((value) => allIds.includes(value)).slice(0, MAX_MANAGER_COMPETITIONS),
                                    },
                                  },
                                }));
                              }}
                            >
                              Tout sélectionner
                            </Button>

                          <Button
                            type="button"
                            variant="text"
                            size="small"
                            onClick={() => {
                              setForm((s) => ({
                                ...s,
                                filtersByDiscipline: {
                                  ...s.filtersByDiscipline,
                                  [discipline]: {
                                    ...s.filtersByDiscipline[discipline],
                                    allowedCompetitionIds: [],
                                    defaultCompetitionIds: [],
                                  },
                                },
                              }));
                            }}
                          >
                            Tout vider
                          </Button>
                        </Stack>

                        <FormControl size="small" fullWidth>
                          <InputLabel>Compétition(s) par défaut</InputLabel>
                          <Select
                            multiple
                            label="Compétition(s) par défaut"
                            value={filters.defaultCompetitionIds}
                            onChange={(e) => {
                              const nextValues = Array.isArray(e.target.value)
                                  ? e.target.value.slice(0, MAX_MANAGER_COMPETITIONS)
                                  : [];

                              setForm((s) => ({
                                ...s,
                                filtersByDiscipline: {
                                  ...s.filtersByDiscipline,
                                  [discipline]: {
                                    ...s.filtersByDiscipline[discipline],
                                    defaultCompetitionIds: nextValues.filter((value) =>
                                      (
                                        s.filtersByDiscipline[discipline]?.allowedCompetitionIds || []
                                      ).includes(value)
                                    ),
                                  },
                                },
                              }));
                            }}
                            renderValue={(selected) =>
                              selected
                                .map((id) => competitionNameById.get(String(id)) || String(id))
                                .join(', ')
                            }
                          >
                            {competitionOptions
                              .filter((competition) =>
                                (filters.allowedCompetitionIds || []).includes(
                                  String(competition._id)
                                )
                              )
                              .map((competition) => {
                                const value = String(competition._id);
                                const label = competition?.season
                                  ? `${competition.name} (${competition.season})`
                                  : competition.name;

                                return (
                                  <MenuItem key={value} value={value}>
                                    {label}
                                  </MenuItem>
                                );
                              })}
                          </Select>
                          <FormHelperText>
                            Maximum 5 compétitions par défaut.
                          </FormHelperText>
                        </FormControl>
                        </Stack>
                      );
                    })}
                </Paper>
              ) : null}


              <TextField
                label="TTL / Durée avant rafraîchissement (secondes)"
                value={form.freshTtlSec}
                onChange={(e) => setForm((s) => ({ ...s, freshTtlSec: e.target.value }))}
                size="small"
                placeholder="ex: 60"
              />

              <FormControl size="small">
                <InputLabel>refreshMode</InputLabel>
                <Select
                  label="refreshMode"
                  value={form.refreshMode}
                  onChange={(e) => setForm((s) => ({ ...s, refreshMode: e.target.value }))}
                >
                  <MenuItem value="on_access">on_access</MenuItem>
                  <MenuItem value="hourly" disabled>hourly</MenuItem>
                </Select>
              </FormControl>

              {!currentConfig && (
                <Stack direction="row" alignItems="center" justifyContent="flex-start" gap={1}>
                  <Typography variant="body2">Archivé</Typography>
                  <Switch
                    checked={!!form.archived}
                    onChange={(e) => setForm((s) => ({ ...s, archived: e.target.checked }))}
                  />
                </Stack>
              )}

              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Actions
                </Typography>

                {currentConfig && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    “Rafraîchir” recalculera immédiatement tous les caches liés à ce widget.
                    “Planifier” recalculera au prochain accès utilisateur.
                  </Typography>
                )}

                <Stack direction="row" justifyContent="flex-start" gap={1} flexWrap="wrap">
                  <Button type="submit" variant="contained" disabled={loadingConfigs}>
                    Enregistrer
                  </Button>

                  {currentConfig && (
                    <>
                      <Button
                        type="button"
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleRefreshNow}
                        disabled={!currentConfig?._id || !!currentConfig?.archived || refreshingNow}
                      >
                        Rafraîchir
                      </Button>

                      <Button
                        type="button"
                        variant="outlined"
                        startIcon={currentConfig?.forceRefresh ? <SyncDisabledIcon /> : <CachedIcon />}
                        onClick={currentConfig?.forceRefresh ? handlePlanUnRefresh : handlePlanRefresh}
                        disabled={!currentConfig?._id || !!currentConfig?.archived || planningRefresh}
                      >
                        {currentConfig?.forceRefresh ? 'Déplannifier' : 'Planifier'}
                      </Button>

                      <Button
                        type="button"
                        variant="outlined"
                        startIcon={currentConfig?.archived ? <UnarchiveIcon /> : <ArchiveIcon />}
                        onClick={handleToggleArchive}
                        disabled={!currentConfig?._id}
                      >
                        {currentConfig?.archived ? 'Désarchiver' : 'Archiver'}
                      </Button>
                    </>
                  )}
                </Stack>
              </Paper>

              {refreshReport && (
                <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                  <Typography variant="subtitle2">Dernier rafraîchissement</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Début : {fmtDate(refreshReport.startedAt)} — Fin : {fmtDate(refreshReport.endedAt)}
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                    <Chip size="small" label={`Total: ${refreshReport.total}`} />
                    <Chip size="small" color="success" label={`Rafraîchis: ${refreshReport.refreshed}`} />
                    <Chip size="small" color="warning" label={`Locks: ${refreshReport.skippedLocked}`} />
                    <Chip size="small" color="default" label={`Sans service: ${refreshReport.skippedNoService}`} />
                    <Chip size="small" color={refreshReport.errors ? 'error' : 'default'} label={`Erreurs: ${refreshReport.errors}`} />
                  </Stack>
                </Paper>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 2.5 }}>
            <Typography variant="h6" gutterBottom>
              Configurations widgets ({configs.length})
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nom</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Mode</TableCell>
                    <TableCell>Archivé</TableCell>
                    <TableCell>Enfants</TableCell>
                    <TableCell align="center">Supprimer</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {configsSorted.map((c) => {
                        const isSelected = selectedConfig?._id === c._id;
                        const isComposed = c.widgetType === 'composed_tabs';
                        const isExpanded = expandedConfigIds.includes(String(c._id));
                        const childRows = Array.isArray(c.childWidgets) ? c.childWidgets : [];

                        return (
                          <React.Fragment key={c._id}>
                            <TableRow
                              hover
                              selected={isSelected}
                              sx={{ cursor: 'pointer' }}
                              onClick={() => {
                                setSelectedConfig(c);
                                setScope(c.scope || DEFAULT_SCOPE);

                                setActiveDisciplineTab(
                                  c.defaultDiscipline && (c.disciplines || []).includes(c.defaultDiscipline)
                                    ? c.defaultDiscipline
                                    : (Array.isArray(c.disciplines) && c.disciplines.length ? c.disciplines[0] : '')
                                );

                                setForm({
                                  configName: c.configName ?? '',
                                  displayTitleMode: c.displayTitleMode ?? 'default',
                                  displayTitle: c.displayTitle ?? '',
                                  widgetType: c.widgetType ?? 'team_ranking',
                                  childWidgetTypes:
                                    Array.isArray(c.childWidgetTypes) && c.childWidgetTypes.length
                                      ? c.childWidgetTypes
                                      : Array.isArray(c.childWidgets) && c.childWidgets.length
                                      ? c.childWidgets.map((child) => child.widgetType).filter(Boolean)
                                      : [],
                                  provider: c.provider ?? 'dsas',
                                  disciplines:
                                    Array.isArray(c.disciplines) && c.disciplines.length
                                      ? c.disciplines
                                      : c.discipline
                                      ? [c.discipline]
                                      : ['rugby'],
                                 defaultDiscipline: c.defaultDiscipline ?? '',
                                  filtersByDiscipline: normalizeFiltersByDiscipline(
                                    Array.isArray(c.disciplines) && c.disciplines.length
                                      ? c.disciplines
                                      : c.discipline
                                      ? [c.discipline]
                                      : ['rugby'],
                                    c.filtersByDiscipline || {}
                                  ),
                                  calendarDateMode: c.calendarDateMode ?? 'fixed',
                                  calendarStartDate: c.calendarStartDate ?? '',
                                  calendarEndDate: c.calendarEndDate ?? '',
                                  calendarDefaultDate: c.calendarDefaultDate ?? '',
                                  directMatchOpenMode: c.directMatchOpenMode || 'cache',
                                  directMatchLiveWindowDaysBefore:
                                    c.directMatchLiveWindowDaysBefore ?? 0,
                                  directMatchLiveWindowDaysAfter:
                                    c.directMatchLiveWindowDaysAfter ?? 1,
                                  ...resolveTtlPolicyFormValues(c.ttlPolicy),
                                  freshTtlSec: c.freshTtlSec ?? '',
                                  staleTtlSec: c.staleTtlSec ?? '',
                                  refreshMode: c.refreshMode ?? 'on_access',
                                  archived: !!c.archived,
                                });

                                setRefreshReport(null);
                              }}
                            >
                              <TableCell>
                                <Stack spacing={0.25}>
                                  <Typography variant="body2">{c.configName || '(sans nom)'}</Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontFamily: 'monospace' }}
                                  >
                                    {c.what_ID}
                                  </Typography>
                                </Stack>
                              </TableCell>

                              <TableCell>{prettyWidgetType(c.widgetType)}</TableCell>
                              <TableCell>{prettyMode(c.refreshMode)}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  color={c.archived ? 'success' : 'default'}
                                  label={c.archived ? 'oui' : 'non'}
                                />
                              </TableCell>

                              <TableCell>
                                {isComposed ? (
                                  <Button
                                    type="button"
                                    size="small"
                                    variant="outlined"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleToggleExpandedConfig(c._id);
                                    }}
                                  >
                                    {isExpanded ? 'Masquer' : `Voir les ${childRows.length} enfants`}
                                  </Button>
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  color="error"
                                  title="Supprimer cette configuration"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteWidgetConfiguration(c);
                                  }}
                                  disabled={
                                      deletingConfigId === String(c.widgetConfigurationId || c.what_ID || '')
                                    }
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>

                            {isComposed && isExpanded ? (
                              <TableRow>
                                <TableCell colSpan={6} sx={{ bgcolor: 'action.hover' }}>
                                  {childRows.length ? (
                                    <Stack spacing={1}>
                                      {childRows.map((child, index) => (
                                        <Paper key={`${child.widgetType}-${index}`} variant="outlined" sx={{ p: 1.5 }}>
                                          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                                            <Chip
                                              size="small"
                                              variant="outlined"
                                              label={prettyWidgetType(child.widgetType)}
                                            />
                                            {child.label ? (
                                              <Chip size="small" label={child.label} />
                                            ) : null}
                                            {child.widgetConfigurationId ? (
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                              >
                                                ID: {child.widgetConfigurationId}
                                              </Typography>
                                            ) : null}
                                            <Box sx={{display: 'flex', flexDirection: 'column' }}>
                                            <Typography variant="caption" color="text.secondary">
                                              Config générée automatiquement.
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              Editer la config parent pour personnaliser les paramètres.
                                            </Typography>
                                            </Box>
                                          </Stack>
                                        </Paper>
                                      ))}
                                    </Stack>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      Aucun enfant généré.
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            ) : null}
                          </React.Fragment>
                        );
                      })}

                  {configs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        Aucune config widget.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Configuration sélectionnée (détail)
            </Typography>

            {currentConfig ? (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {currentConfig.configName || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {prettyWidgetType(currentConfig.widgetType)}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                      label={currentConfig.archived ? 'Archivé' : 'Actif'}
                      color={currentConfig.archived ? 'warning' : 'success'}
                      size="small"
                    />
                    <Chip
                      label={`Provider: ${currentConfig.provider || '-'}`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`Discipline par défaut: ${currentConfig.defaultDiscipline || '-'}`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={`Mode: ${prettyMode(currentConfig.refreshMode ?? defaultTtls?.refreshMode)}`}
                      size="small"
                      variant="outlined"
                    />
                    {currentConfig.forceRefresh && (
                      <Chip
                        label="Rafraîchissement planifié"
                        color="info"
                        size="small"
                      />
                    )}
                  </Stack>
                  <Button
                    type="button"
                    variant="contained"
                    onClick={handleOpenExportDialog}
                    disabled={!currentConfig?.builderPath}
                  >
                    Exporter
                  </Button>

                  <Divider />

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      TTL effectifs
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {ttlSummary.map((t) => (
                        <Chip
                          key={t.label}
                          label={`${t.label}: ${t.value}s`}
                          size="small"
                          variant={t.origin === 'override' ? 'filled' : 'outlined'}
                        />
                      ))}
                    </Stack>
                  </Box>

                  <Divider/>
                  
                    <Box>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Typography variant="subtitle2" gutterBottom>
                        Titre affiché sur le widget
                      </Typography>
                      <Chip
                            size="small"
                            label={currentConfig.displayTitleMode}
                            variant="outlined"
                          />  
                          </Stack>

                      <Stack direction="row" spacing={1} flexWrap="wrap">
                          
                          {currentConfig.displayTitle && (
                            <Chip
                              size="small"
                              label={currentConfig.displayTitle}
                            />
                          )}
                      </Stack>
                    </Box>

                  <Divider />

                  {currentConfig.widgetType === 'composed_tabs' ? (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Onglets configurés
                      </Typography>

                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {(currentConfig.childWidgetTypes || []).map((widgetType) => (
                          <Chip
                            key={widgetType}
                            size="small"
                            variant="outlined"
                            label={prettyWidgetType(widgetType)}
                          />
                        ))}
                      </Stack>
                    </Box>
                  ) : null}

                  {(
                    currentConfig.widgetType === 'direct_calendar' ||
                    (
                      currentConfig.widgetType === 'composed_tabs' &&
                      Array.isArray(currentConfig.childWidgetTypes) &&
                      currentConfig.childWidgetTypes.includes('direct_calendar')
                    )
                  ) ? (
                    <>
                      <Divider />

                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Options direct — Calendrier direct
                        </Typography>
 
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`Mode: ${currentConfig.calendarDateMode || '-'}`}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`Début: ${currentConfig.calendarStartDate || '-'}`}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`Fin: ${currentConfig.calendarEndDate || '-'}`}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`Défaut: ${currentConfig.calendarDefaultDate || '-'}`}
                          />
                          {buildTtlPolicyChips(currentConfig.ttlPolicy).map((label) => (
                            <Chip
                              key={label}
                              size="small"
                              variant="outlined"
                              label={label}
                            />
                          ))}
                        </Stack>
                      </Box>
                    </>
                  ) : null}

                  <Divider />

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Disciplines configurées
                    </Typography>

                    <Stack spacing={1.5}>
                      {(currentConfig.disciplines || []).map((discipline) => {
                        const disciplineFilters =
                          currentConfig.filtersByDiscipline?.[discipline] || {};

                        const allowedSeasons = disciplineFilters.allowedSeasons || [];
                        const defaultSeasons = disciplineFilters.defaultSeasons || [];
                        const allowedCompetitionIds =
                          disciplineFilters.allowedCompetitionIds || [];
                        const defaultCompetitionIds =
                          disciplineFilters.defaultCompetitionIds || [];

                        return (
                          <Paper
                            key={discipline}
                            variant="outlined"
                            sx={{ p: 1.5, bgcolor: 'background.default' }}
                          >
                            <Stack spacing={1}>
                              <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                                <Chip
                                  size="small"
                                  label={discipline}
                                  color={
                                    discipline === currentConfig.defaultDiscipline
                                      ? 'primary'
                                      : 'default'
                                  }
                                />
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={
                                    disciplineFilters.seasonMode === 'since'
                                      ? `Saisons depuis ${disciplineFilters.seasonSince || '-'}`
                                      : `${allowedSeasons.length} saison(s) figée(s)`
                                  }
                                />
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`${allowedCompetitionIds.length} compétition(s) figée(s)`}
                                />
                              </Stack>

                              {disciplineFilters.seasonMode === 'explicit' ? (
                                <>
                                  <Typography variant="body2" color="text.secondary">
                                    Saisons par défaut :{' '}
                                    {defaultSeasons.length ? defaultSeasons.join(', ') : '-'}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Saisons figées :{' '}
                                    {allowedSeasons.length ? allowedSeasons.join(', ') : '-'}
                                  </Typography>
                                </>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  Chargement des saisons à partir de :{' '}
                                  {disciplineFilters.seasonSince || '-'}
                                </Typography>
                              )}

                              <Typography variant="body2" color="text.secondary">
                                Compétitions par défaut : {defaultCompetitionIds.length}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Compétitions figées : {allowedCompetitionIds.length}
                              </Typography>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Informations techniques
                    </Typography>

                    <Stack spacing={0.5}>
                      <Typography variant="body2" color="text.secondary">
                        what_ID: {currentConfig.what_ID}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        widgetConfigurationId: {currentConfig.widgetConfigurationId || '-'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Créée le : {fmtDate(currentConfig.createdAt)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Mise à jour le : {fmtDate(currentConfig.updatedAt)}
                      </Typography>

                      {currentConfig.builderPath ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ wordBreak: 'break-all' }}
                        >
                          Builder URL : {currentConfig.builderPath}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Aucune configuration widget trouvée.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              Données des caches widgets
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {selectedConfig
                ? `Widget: ${selectedConfig.configName || selectedConfig.what_ID}`
                : 'Sélectionner une configuration au dessus pour afficher ses caches.'}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {loadingCaches ? 'Chargement...' : `${sortedCaches.length} cache(s)`}
              {selectedCacheIds.length > 0 ? ` — ${selectedCacheIds.length} sélectionné(s)` : ''}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRefreshSelectedCaches}
              disabled={
                !selectedConfig ||
                !selectedCacheIds.length ||
                refreshingSelectedCaches ||
                deletingSelectedCaches
              }
            >
              Rafraîchir maintenant
            </Button>

            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleDeleteSelectedCaches}
              disabled={
                !selectedConfig ||
                !selectedCacheIds.length ||
                refreshingSelectedCaches ||
                deletingSelectedCaches
              }
            >
              Supprimer la sélection
            </Button>
          </Stack>
        </Stack>

        {selectedCachesRefreshReport && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2">
              Dernier rafraîchissement unitaire
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Début : {fmtDate(selectedCachesRefreshReport.startedAt)} — Fin : {fmtDate(selectedCachesRefreshReport.endedAt)}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Durée : {fmtDuration(selectedCachesRefreshReport.durationMs)}
            </Typography>
          </Paper>
        )}

        {selectedCachesDeleteReport && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2">
              Dernière suppression groupée
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Fin : {fmtDate(selectedCachesDeleteReport.endedAt)}
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
              <Chip
                size="small"
                label={`Demandés: ${selectedCachesDeleteReport.requested}`}
              />
              <Chip
                size="small"
                color="success"
                label={`Supprimés: ${selectedCachesDeleteReport.deleted}`}
              />
              <Chip
                size="small"
                color={selectedCachesDeleteReport.errors ? 'error' : 'default'}
                label={`Erreurs: ${selectedCachesDeleteReport.errors}`}
              />
            </Stack>

            {selectedCachesDeleteReport.details?.length ? (
              <Box sx={{ mt: 1 }}>
                {selectedCachesDeleteReport.details.map((detail) => (
                  <Typography
                    key={detail.cacheId}
                    variant="caption"
                    color="error"
                    display="block"
                  >
                    {detail.cacheId}: {detail.error}
                  </Typography>
                ))}
              </Box>
            ) : null}
          </Paper>
        )}

        <TableContainer component={Paper} variant="outlined">
          {!selectedConfig ? (
            <Typography variant="body2" color="text.secondary">
              Aucun cache affiché.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allCachesSelected}
                      indeterminate={someCachesSelected}
                      onChange={handleToggleAllCaches}
                      disabled={!selectedConfig || sortedCaches.length === 0}
                    />
                  </TableCell>
                  <TableCell>Données du cache</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell>Rafraîchissement en cours</TableCell>
                  <TableCell>Date de dernier rafraîchissement</TableCell>
                  <TableCell>Etat</TableCell>
                  <TableCell>Age</TableCell>
                  <TableCell align="center">Supprimer</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedCaches.map((row) => {
                  const isChecked = !!row._id && selectedCacheIds.includes(String(row._id));

                  return (
                    <TableRow key={row._id || row.requestId} hover selected={isChecked}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleToggleOneCache(row._id)}
                          disabled={!row._id}
                        />
                      </TableCell>

                      <TableCell sx={{ minWidth: 240 }}>
                        <Stack spacing={0.5}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            {row.contextLabel ? (
                              <Chip size="small" label={row.contextLabel} />
                            ) : (
                              <Chip size="small" variant="outlined" label="sans label" />
                            )}

                            {row.widgetType ? (
                              <Chip size="small" variant="outlined" label={prettyWidgetType(row.widgetType)} />
                            ) : null}

                            {/* {row.configType ? (
                              <Chip size="small" variant="outlined" label={row.configType} />
                            ) : null} */}
                          </Stack>

                          {row._context_summary ? (
                            <Typography variant="caption" color="text.secondary">
                              {row._context_summary}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Aucun contexte enregistré
                            </Typography>
                          )}
                          {/*TODO baseUrl func*/}
                          {row.pagePath ? (
                            <Button
                              type="button"
                              size="small"
                              variant="outlined"
                              onClick={() => window.open((process.env.NODE_ENV === "development"?  "http://localhost:3006" : process.env.REACT_APP_LIVE_CLIENT_URL  || window.location.origin) +row.pagePath, '_blank', 'noopener,noreferrer')}
                              sx={{ alignSelf: 'flex-start', minWidth: 0, px: 0 }}
                            >
                              Ouvrir URL
                            </Button>
                          ) : null}
                        </Stack>
                      </TableCell>

                      <TableCell>{row.scope}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={row.isProcessing ? 'success' : 'default'}
                          label={row.isProcessing ? 'oui' : 'non'}
                        />
                      </TableCell>
                      <TableCell>
                        {row.createdAt ? new Date(row.createdAt).toLocaleString() : ''}
                      </TableCell>
                      <TableCell>{row._state}</TableCell>
                      <TableCell>{row._ageLabel}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          title="Supprimer ce cache"
                          onClick={() => handleDeleteCache(row._id, row.contextLabel)}
                          disabled={!row._id || deletingCacheId === String(row._id) || row.isProcessing}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {sortedCaches.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Aucun cache trouvé.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>
      <Dialog
        open={exportDialogOpen}
        onClose={handleCloseExportDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Exporter le widget</DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Choisissez le format à copier pour intégrer ce widget.
            </Typography>

            {copyFeedback ? (
              <Alert severity={copyFeedbackType}>{copyFeedback}</Alert>
            ) : null}

            <TextField
              label="URL du builder"
              value={getFinalUrl()}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
            />

            <Stack spacing={1}>
              <Button type="button" variant="contained" onClick={onCopyURL}>
                Copier URL
              </Button>

              <Button
                type="button"
                variant="outlined"
                onClick={onCopyIframeSmartScroll}
              >
                Copier iframe scroll intelligent
              </Button>

              <Button
                type="button"
                variant="outlined"
                onClick={onCopyIframe}
              >
                Copier iframe
              </Button>
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseExportDialog}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WidgetCacheManager;